import React, { useState, useEffect } from "react";
import { Box, Paper, TextField, Button, Stack, Typography, Divider, Chip } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { kbService } from "../../api/services/kbService";
import { PageHeader, StatusChip, Loader } from "../../components/Common";

export default function KbArticleDetail() {
  const { articleId } = useParams();
  const id = Number(articleId);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const { data: article, isLoading } = useQuery({ queryKey: ["kb-article", id], queryFn: () => kbService.getArticle(id) });
  const { data: versions } = useQuery({ queryKey: ["kb-versions", id], queryFn: () => kbService.listVersions(id) });

  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");

  useEffect(() => {
    if (article) { setTitle(article.title); setContent(article.content); }
  }, [article]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["kb-article", id] });
    queryClient.invalidateQueries({ queryKey: ["kb-versions", id] });
  };

  const saveMutation = useMutation({
    mutationFn: () => kbService.updateArticle(id, { title, content }),
    onSuccess: () => { enqueueSnackbar("Saved (new version created)", { variant: "success" }); invalidate(); },
  });
  const submitMutation = useMutation({
    mutationFn: () => kbService.submitForReview(id),
    onSuccess: () => { enqueueSnackbar("Submitted for review", { variant: "success" }); invalidate(); },
  });
  const publishMutation = useMutation({
    mutationFn: () => kbService.publish(id),
    onSuccess: () => { enqueueSnackbar("Published", { variant: "success" }); invalidate(); },
  });

  if (isLoading) return <Loader label="Loading article..." />;
  if (!article) return null;

  return (
    <Box>
      <PageHeader
        title={article.title}
        subtitle={`v${article.current_version} · ${article.view_count} views`}
        action={<StatusChip value={article.status} />}
      />

      <Stack direction="row" spacing={3}>
        <Paper variant="outlined" sx={{ p: 2.5, flex: 1 }}>
          <Stack spacing={2}>
            <TextField label="Title" value={title} onChange={(e) => setTitle(e.target.value)} fullWidth />
            <TextField label="Content" value={content} onChange={(e) => setContent(e.target.value)} multiline minRows={10} fullWidth />
            <Stack direction="row" spacing={1.5}>
              <Button variant="contained" disabled={saveMutation.isPending} onClick={() => saveMutation.mutate()}>Save</Button>
              {article.status === "draft" && (
                <Button variant="outlined" disabled={submitMutation.isPending} onClick={() => submitMutation.mutate()}>
                  Submit for Review
                </Button>
              )}
              {(article.status === "pending_review" || article.status === "draft") && (
                <Button variant="outlined" color="success" disabled={publishMutation.isPending} onClick={() => publishMutation.mutate()}>
                  Publish
                </Button>
              )}
              <Button color="inherit" onClick={() => navigate("/kb")}>Back to list</Button>
            </Stack>
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2.5, width: 280, flexShrink: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>Version History</Typography>
          <Stack spacing={1.5} divider={<Divider />}>
            {(versions || []).map((v) => (
              <Box key={v.id}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip size="small" label={`v${v.version_number}`} />
                  <Typography variant="caption" color="text.secondary">
                    {new Date(v.created_at).toLocaleString()}
                  </Typography>
                </Stack>
              </Box>
            ))}
            {(!versions || versions.length === 0) && (
              <Typography variant="body2" color="text.secondary">No history yet</Typography>
            )}
          </Stack>
        </Paper>
      </Stack>
    </Box>
  );
}
